import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'

import { HelpCircle, LogOut, Settings, Users } from 'lucide-react'

import { hasRole } from '@/lib/roles'
import { capitalizeName } from '@/lib/utils'

import { useAuthStore } from '@/stores/auth'

import { paths } from '@/config/paths'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { SmartAvatar } from '@/components/ui-custom/SmartAvatar'
import { SmartButton } from '@/components/ui-custom/SmartButton'

export default function UserDropdown() {
  const { t } = useTranslation(['common'])
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const logout = useAuthStore((state) => state.logout)

  const handleLogout = async () => {
    await logout()
  }

  // Role-based menu items
  const menuItems = useMemo(() => {
    const isMember = hasRole(user, 'member')
    const isStaff = hasRole(user, ['super_admin', 'gym_manager', 'assistant', 'coach'])

    const items = [
      {
        key: 'account',
        icon: <Settings size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.account', { ns: 'common', defaultValue: 'Account settings' }),
        onSelect: () => navigate(paths.dashboard.profile.root.getHref()),
        roles: ['member', 'super_admin', 'gym_manager', 'assistant', 'coach'],
      },
    ]

    // Member-specific menu items
    if (isMember) {
      items.push({
        key: 'my-bookings',
        icon: <Users size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.myBookings', { ns: 'common', defaultValue: 'My Bookings' }),
        onSelect: () => navigate(paths.dashboard.member.myBookings.getHref()),
        roles: ['member'],
      })
    }

    // Staff-specific menu items
    if (isStaff) {
      items.push({
        key: 'bookings',
        icon: <Users size={14} className='opacity-60' aria-hidden='true' />,
        label: t('userDropdown.menu.bookings', { ns: 'common', defaultValue: 'Manage Bookings' }),
        onSelect: () => navigate(paths.dashboard.bookings.root.getHref()),
        roles: ['super_admin', 'gym_manager', 'assistant', 'coach'],
      })
    }

    // Common menu items
    items.push({
      key: 'help',
      icon: <HelpCircle size={14} className='opacity-60' aria-hidden='true' />,
      label: t('userDropdown.menu.help', { ns: 'common', defaultValue: 'Help' }),
      onSelect: () => navigate(paths.dashboard.support.root.getHref()),
      roles: ['member', 'super_admin', 'gym_manager', 'assistant', 'coach'],
    })

    return items
  }, [user, t, navigate])

  const logoutItem = {
    key: 'logout',
    icon: <LogOut size={14} className='opacity-60' aria-hidden='true' />,
    label: t('userDropdown.menu.logOut', { ns: 'common', defaultValue: 'Log Out' }),
    onClick: (e: React.SyntheticEvent) => {
      e.preventDefault()
      handleLogout()
    },
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SmartButton variant='ghost' className='h-auto p-0 hover:bg-transparent'>
          <SmartAvatar size='sm' name={user?.full_name} alt={user?.full_name} src={user?.profile_photo!} ring={false} />
        </SmartButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='max-w-68' align='end'>
        <DropdownMenuLabel className='flex min-w-0 flex-col'>
          <span className='text-foreground truncate text-sm font-medium'>
            {user?.full_name ? capitalizeName(user?.full_name) : 'N/A'}
          </span>
          <span className='text-muted-foreground truncate text-xs font-normal'>{user?.email ?? null}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {menuItems.map(({ key, icon, label, onSelect }) => (
            <DropdownMenuItem key={key} onSelect={onSelect} className='cursor-pointer'>
              {icon}
              <span>{label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logoutItem.onClick} className='cursor-pointer'>
          {logoutItem.icon}
          <span>{logoutItem.label}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
